<template>
  <Wrapper hasChildren @openEditor="$emit('openEditor')"  @openChildEditor="openChildEditor">
    <div class="form-container">
      <div class="title" :class="!title && 'no-wrapper'">
        <div class="title-text module-text-title" ref="titleEl">{{ title }}</div>
        <div class="under-line" :style="titleUnderLineStyle">
          <el-divider v-if="hasTitleUnderLine"></el-divider>
        </div>
      </div>
      <div class="sub-title" :class="!subTitle && 'no-wrapper'">
        <div class="sub-title-text module-text-subtitle" ref="subTitleEl">{{ subTitle }}</div>
        <div class="under-line" :style="subTitleUnderLineStyle">
          <el-divider v-if="hasSubTitleUnderLine"></el-divider>
        </div>
      </div>
      <div :class="[`form-${formKey}`, 'form-list']">
        <WrappedForm ref="wrappedForm" labelPosition="top" requiredAsteriskPosition="before" :model="formData" :formLines="formLine">
        </WrappedForm>
      </div>
      <div class="form-submit-btn-container">
        <el-button class="submit-btn custom-btn" :class="this.isSubmit && !this.canSubmit && 'disabled'" @click="handelSubmitForm">{{ submitContent }}</el-button>
      </div>
    </div>
  </Wrapper>
</template>

<script>
import _ from 'lodash'
import Sortable from 'sortablejs'
import { mapGetters, mapActions } from 'vuex'
import { childName } from '../index'
import { deviceType, appendSourceCode } from '@/utils/common'
import { FORM_ITEM_TYPE_LIST } from '@/constant/crm'
import Wrapper from '../../../components/render/wrapper.vue'
import WrappedForm from '@/components/formEditor/wrappedForm.vue'
import childRender from './childRender.vue'
import moment from 'moment'
import { mixedRouterPush } from '@/router/routes'
import { ACCOUNT_TYPE } from '@/constant/common'

export default {
  components: {
    Wrapper,
    WrappedForm
  },
  props: {
    title: {
      type: String,
      default: ''
    },
    subTitle: {
      type: String,
      default: ''
    },
    submitBtnContent: {
      type: String,
      default: ''
    },
    submitUrl: {
      type: String,
      default: ''
    },
    nextPage: {
      type: Object,
      required: false
    },
    underLine: {
      type: Object,
      required: false
    },
    styleConfig: {
      type: Object,
      required: false
    },
    formKey: {
      type: String
    },
    renderMode: {
      type: String
    },
    children: {
      type: Array
    },
    needChildren: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      formData: {},
      formItemInfo: {},
      isSubmit: false,
      canSubmit: true
    }
  },
  computed: {
    ...mapGetters('microSite', ['isEditMode', 'currentPage', 'currentPageType', 'companyInfo', 'pagePathPrefix']),
    siteId () {
      return this.$route.params.siteId
    },
    hasTitleUnderLine () {
      return this.underLine && this.underLine.title && this.underLine.title.isShow
    },
    hasSubTitleUnderLine () {
      return this.underLine && this.underLine.subTitle && this.underLine.subTitle.isShow
    },
    titleUnderLineStyle () {
      return this.formatUnderLineStyle('title', this.underLine.title)
    },
    subTitleUnderLineStyle () {
      return this.formatUnderLineStyle('subTitle', this.underLine.subTitle)
    },
    submitContent () {
      if (this.isSubmit && !this.canSubmit) {
        return this.$t('common.btn.edit')
      }
      return this.submitBtnContent || this.$t('common.btn.submit')
    },
    formLine () {
      const lines = {}
      const vm = this
      _.forEach(this.children, (item) => {
        const props = item.data.props
        let rules = [{ required: props.isRequired, message: this.$t('microSite.form.message.isRequired'), trigger: 'change' }]
        if (props.type === FORM_ITEM_TYPE_LIST.LOCATION.value) {
          rules = [{ required: props.isRequired, validator: this.validateLocation, trigger: 'blur' }]
        } else if (props.type === FORM_ITEM_TYPE_LIST.LINK.value) {
          rules = [{ required: props.isRequired, validator: this.validateLink, trigger: 'blur' }]
        }
        lines[props.itemKey] = {
          elType: childRender,
          label: props.title,
          class: _.toLower(props.type),
          rules: rules,
          get disabled () {
            return vm.isSubmit && !vm.canSubmit
          },
          props: {
            ...props,
            value: this.formData[props.itemKey]
          },
          events: {
            openChildEditor: () => this.openChildEditor(item)
          }
        }
      })
      return lines
    },
    currentFormItemKeyList () {
      const keyList = []
      _.forEach(this.children, (item) => {
        const props = item.data.props
        keyList.push(props.itemKey)
      })
      return keyList
    },
    hasLogin () {
      const account = this.getCurrentAccount()
      return Boolean(account?.id)
    },
    accountType () {
      if (this.hasLogin) {
        return this.getCurrentAccount().type
      }
      return ''
    },
    needLogin () {
      if (this.isEditMode) {
        return false
      }
      return Boolean(!this.hasLogin)
    },
    accountFormKey () {
      return `${this.hasLogin}_${this.siteId}_${this.formKey}`
    },
    isiOS () {
      return deviceType.isiOS
    }
  },
  async created () {
    if (this.needLogin) {
      this.$router.push({ path: `/passport/${ACCOUNT_TYPE.CANDIDATE}/register`, query: { company_id: this.companyInfo.id, back: this.$route.fullPath } })
    } else {
      await this.initFormData()
      if (this.accountType === 'candidate') {
        if (this.isiOS) {
          window.addEventListener('pageshow', this.handelReloadPage)
        }
        window.addEventListener(this.isiOS ? 'pagehide' : 'beforeunload', this.handelSaveDataCache)
      }
    }
  },
  methods: {
    ...mapGetters('account', ['getCurrentAccount']),
    ...mapGetters('crm', ['getMicrositeAdditionalInfo']),
    ...mapActions('crm', ['changeAccountMicrositeAdditionalInfo']),
    ...mapActions('microSite', ['getMicrositeFormSubmitData', 'submitMicrositeFormData', 'updateMicrositeFormData']),
    formatUnderLineStyle (type, config, isSelfTigger) {
      let width = config.width
      let el = null
      if (this.$refs) {
        if (type === 'title') {
          el = this.$refs.titleEl
        } else if (type === 'subTitle') {
          el = this.$refs.subTitleEl
        }
      }
      if (el) {
        if (!width || width === 'full') {
          width = '100%'
        } else if (!isNaN(width)) {
          width = width + 'px'
        } else if (width === 'compute') {
          width = (el.clientWidth + 60) + 'px'
        }
        const underLineEl = el.nextElementSibling.firstElementChild
        if (underLineEl) {
          const style = underLineEl.style
          style.width = width
          style.backgroundColor = config.color || '#DCDFE6'
        }
      } else if (!isSelfTigger) {
        this.$nextTick(() => this.formatUnderLineStyle(type, config, true))
      }
      return { }
    },
    openChildEditor (item) {
      this.$emit('openChildEditor', {
        editorName: childName,
        fromChild: false,
        customChildRenderData: item
      })
    },
    async handelSubmitForm () {
      if (!this.canSubmit) {
        this.canSubmit = true
        return
      }
      const isValid = await this.$refs.wrappedForm?.submit()
      if (this.isEditMode || !isValid) {
        return
      }
      const submitInfoList = []
      _.forEach(this.formData, (item, key) => {
        if (!this.currentFormItemKeyList.includes(key)) {
          return
        }
        submitInfoList.push({
          ...this.formItemInfo[key],
          value: item
        })
      })
      const params = {
        site_id: this.siteId,
        microsite_type: this.currentPageType,
        form_key: this.formKey,
        submit_info_list: submitInfoList
      }
      const resp = await this.submitMicrositeFormData(params)
      if (resp) {
        this.$message.success(this.$t('common.message.submitSuccess'))
        this.isSubmit = true
        this.canSubmit = false
        if (!this.nextPage || !this.nextPage.isActive) {
          return
        }
        if (this.nextPage.url.startsWith('/v/onboarding/questionnaire')) {
          this.$eventBus.$emit('openOnboardingQuestionnairePage', this.nextPage.openNewWindow)
          return
        }
        let targetUrl = null
        if (this.nextPage.isInnerPage) {
          targetUrl = this.pagePathPrefix + this.nextPage.url
        } else {
          targetUrl = this.nextPage.url
        }
        targetUrl = appendSourceCode(targetUrl)
        if (this.nextPage.openNewWindow) {
          window.open(targetUrl)
        } else {
          if (this.nextPage.isInnerPage) {
            this.$router.push(targetUrl)
          } else {
            mixedRouterPush(targetUrl)
          }
        }
      }
    },
    async initFormData () {
      let formData = {}
      const formItemInfo = {}
      const formItemKeyList = this.currentFormItemKeyList
      _.forEach(this.children, (item) => {
        const props = item.data.props
        if (formItemKeyList.includes(props.itemKey)) {
          formData[props.itemKey] = ''
          formItemInfo[props.itemKey] = {
            key: props.itemKey,
            title: props.title,
            type: props.type,
            link_setting: props.linkSetting
          }
        }
      })

      if (this.accountType === 'candidate') {
        const resp = await this.getMicrositeFormSubmitData({ site_id: this.siteId, form_key: this.formKey })
        if (resp && resp.submit_record) {
          _.forEach(resp.submit_record, (item, key) => {
            if (!formItemKeyList.includes(key)) {
              return
            }
            if (formItemInfo[key]?.type === FORM_ITEM_TYPE_LIST.LINK.value) {
              formData[key] = item.status
            } else {
              formData[key] = item
            }
          })
          if (!_.isEmpty(resp.submit_record)) {
            this.isSubmit = true
            this.canSubmit = false
          } else {
            const micrositeAdditionalInfo = this.getMicrositeAdditionalInfo()
            if (micrositeAdditionalInfo[this.accountFormKey]?.expire && moment().isBefore(moment.unix(micrositeAdditionalInfo[this.accountFormKey].expire))) {
              formData = micrositeAdditionalInfo[this.accountFormKey].data
            }
          }
        }
      }
      this.formData = formData
      this.formItemInfo = formItemInfo
    },
    handelChildEditor () {
      this.$emit('openChildEditor', {
        editorName: childName,
        fromChild: true
      })
    },
    validateLocation (rule, value, callback) {
      if (!rule.required) {
        callback()
      } else if (!value || !value.country || !value.province || !value.city) {
        callback(new Error(this.$t('microSite.form.message.locationRequired')))
      } else {
        callback()
      }
    },
    validateLink (rule, value, callback) {
      if (!rule.required) {
        callback()
      } else if (!value) {
        callback(new Error(this.$t('microSite.form.message.policyLinkRequired')))
      } else {
        callback()
      }
    },
    handelSaveDataCache (event) {
      this.changeAccountMicrositeAdditionalInfo({
        id: this.accountFormKey,
        info: {
          expire: moment().add(10, 'minutes').format('X'),
          data: this.formData
        }
      })
    },
    handelReloadPage (event) {
      if (event.persisted) {
        window.location.reload()
      }
    }
  },
  mounted () {
    const form = document.querySelector(`.render-wrapper .form-container .form-${this.formKey} form`)
    Sortable.create(form, {
      handle: '.draggable-handle',
      animation: 200,
      onEnd: ({ newIndex, oldIndex }) => {
        this.$emit('changeChildrenOrder', { newIndex, oldIndex })
      }
    })
  },
  destroyed () {
    if (this.isiOS) {
      window.removeEventListener('pageshow', this.handelReloadPage)
      window.removeEventListener('pagehide', this.handelSaveDataCache)
    } else {
      window.removeEventListener('beforeunload', this.handelSaveDataCache)
    }
  }
}
</script>

<style lang="scss" scoped>
.site-common {
  .form-container {
    text-align: center;
    margin: 35px 0;

    .under-line {
      width: 100%;

      .el-divider {
        margin: 0 auto;
      }
    }

    .title, .sub-title {
      margin-bottom: 20px;
    }

    .title-text, .sub-title-text {
      display: inline-block;
      font-weight: unset;
    }

    .sub-title-text {
      padding: 0 15px;
    }

    .form-submit-btn-container {
      .submit-btn {
        &.disabled {
          background: #F5F7FA;
          border-color: #F5F7FA;
          color: #606266;
        }
      }
    }
  }
  .form-list {
    ::v-deep .el-form {
      .el-form-item {
        padding-bottom: 5px;
        margin-bottom: 17px;
        position: relative;

        .el-form-item__content {
          position: unset;

          .child-wrapper {
            position: unset;
          }
        }

        .el-form-item__label {

          &::before {
            vertical-align: top;
          }

          .label-inner {
            width: 94%;
          }
        }

        &.link {
          .el-form-item__label {
            display: none;
          }
        }
      }
    }
  }
}
.site-mobile {
  .form-container {
    padding: 0 10px;

    .title {
      padding-bottom: 1px;

      .under-line {
        margin-top: 10px;
      }
    }

    .form-submit-btn-container {
      .submit-btn {
        width: 100%;
        margin: 20px 0;
        color: #FFFFFF;
      }
    }
  }
  .form-list {
    ::v-deep .el-form {
      .el-form-item__label {
        width: 100%;
      }
    }
  }
}
.site-pc {
  .form-container {
    width: 1080px;
    margin: 0 auto;

    .title {
      .under-line {
        margin-top: 20px;
      }
    }

    .submit-btn {
      width: 35%;
      margin: 20px 0;
      font-size: 18px;
    }
  }
  .form-list {
    ::v-deep .el-form {
      .el-form-item {
        .el-form-item__label {
          width: 35%;
        }
      }

      .el-form-item__error {
        margin-left: 32.5%;
      }
    }
  }
}
</style>
